import React, { createContext, ReactNode, useContext } from 'react';
import api from '../services/api';
import { Review, ReviewCreationAttributes } from '../models/Review';

interface ReviewContextType {
  createReview: (reviewData: ReviewCreationAttributes) => Promise<Review>;
  getReviewsByProduct: (
    productId: number,
    page?: number,
    limit?: number
  ) => Promise<{
    reviews: Review[];
    total: number;
    page: number;
    totalPages: number;
    averageRating: number;
  }>;
  getUserReviews: (
    page?: number,
    limit?: number
  ) => Promise<{
    reviews: Review[];
    total: number;
    page: number;
    totalPages: number;
  }>;
  verifyReview: (
    id: string,
    verified: boolean
  ) => Promise<Review>;
  deleteReview: (id: string) => Promise<void>;
}

const ReviewContext = createContext<ReviewContextType | undefined>(undefined);

export const useReview = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReview must be used within a ReviewProvider');
  }
  return context;
};

interface ReviewProviderProps {
  children: ReactNode;
}

export const ReviewProvider: React.FC<ReviewProviderProps> = ({ children }) => {
  const reactAppApiUrl = process.env.REACT_APP_API_URL;
  if (!reactAppApiUrl) {
    throw new Error('REACT_APP_API_URL is not set');
  }

  const apiUrl = new URL('reviews/', reactAppApiUrl).toString();

  /**
   * Tworzy nową recenzję dla produktu
   */
  const createReview = async (
    reviewData: ReviewCreationAttributes
  ): Promise<Review> => {
    try {
      const response = await api.post(new URL('create', apiUrl).toString(), reviewData);
      return response.data.review;
    } catch (error: any) {
      console.error('Failed to create review', error);
      throw new Error(error.response?.data?.message || 'Błąd podczas dodawania recenzji');
    }
  };

  /**
   * Pobiera recenzje dla danego produktu
   */
  const getReviewsByProduct = async (
    productId: number,
    page = 1,
    limit = 10
  ): Promise<{
    reviews: Review[];
    total: number;
    page: number;
    totalPages: number;
    averageRating: number;
  }> => {
    try {
      const response = await api.get(new URL(`product/${productId}`, apiUrl).toString(), {
        params: { page, limit }
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch reviews by product', error);
      throw new Error(error.response?.data?.message || 'Błąd podczas pobierania recenzji produktu');
    }
  };

  /**
   * Pobiera recenzje zalogowanego użytkownika
   */
  const getUserReviews = async (
    page = 1,
    limit = 10
  ): Promise<{
    reviews: Review[];
    total: number;
    page: number;
    totalPages: number;
  }> => {
    try {
      const response = await api.get(new URL('my-reviews', apiUrl).toString(), {
        params: { page, limit }
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch user reviews', error);
      throw new Error(error.response?.data?.message || 'Błąd podczas pobierania recenzji użytkownika');
    }
  };

  /**
   * Weryfikuje recenzję (tylko dla administratorów)
   */
  const verifyReview = async (
    id: string,
    verified: boolean
  ): Promise<Review> => {
    try {
      const response = await api.put(new URL(`${id}/verify`, apiUrl).toString(), {
        verified
      });
      return response.data.review;
    } catch (error: any) {
      console.error('Failed to verify review', error);
      throw new Error(error.response?.data?.message || 'Błąd podczas weryfikacji recenzji');
    }
  };

  /**
   * Usuwa recenzję (tylko dla administratorów)
   */
  const deleteReview = async (id: string): Promise<void> => {
    try {
      await api.delete(new URL(`${id}`, apiUrl).toString());
    } catch (error: any) {
      console.error('Failed to delete review', error);
      throw new Error(error.response?.data?.message || 'Błąd podczas usuwania recenzji');
    }
  };

  return (
    <ReviewContext.Provider
      value={{
        createReview,
        getReviewsByProduct,
        getUserReviews,
        verifyReview,
        deleteReview
      }}
    >
      {children}
    </ReviewContext.Provider>
  );
};
